import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { API_BASE } from "@/lib/utils";

import { formatMoneyDisplay } from "@/lib/money-display";

const CATEGORY_LABELS = {
  email: "Email credits",
  capacity: "Capacity",
  integrations: "Integrations",
  services: "Professional services",
  delivery: "Success programs",
};

export default function BillingAddOnCart({ catalog = [], revolutEnabled, onCheckoutComplete }) {
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);

  const catalogById = useMemo(() => {
    const map = new Map();
    for (const item of catalog) {
      map.set(item.id, item);
    }
    return map;
  }, [catalog]);

  const groupedCatalog = useMemo(() => {
    const groups = new Map();
    for (const item of catalog) {
      const cat = item.category || "services";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(item);
    }
    return Array.from(groups.entries());
  }, [catalog]);

  const cartLines = useMemo(
    () =>
      cart
        .map((line) => {
          const product = catalogById.get(line.id);
          if (!product) return null;
          return { ...product, qty: line.qty };
        })
        .filter(Boolean),
    [cart, catalogById],
  );

  const subtotal = useMemo(
    () => cartLines.reduce((sum, line) => sum + (line.priceSgd || 0) * line.qty, 0),
    [cartLines],
  );

  const addToCart = (id) => {
    setCart((prev) => {
      const existing = prev.find((l) => l.id === id);
      if (existing) {
        return prev.map((l) => (l.id === id ? { ...l, qty: l.qty + 1 } : l));
      }
      return [...prev, { id, qty: 1 }];
    });
    toast.message("Added to cart", { description: catalogById.get(id)?.name });
  };

  const setQty = (id, qty) => {
    if (qty < 1) {
      setCart((prev) => prev.filter((l) => l.id !== id));
      return;
    }
    setCart((prev) => prev.map((l) => (l.id === id ? { ...l, qty } : l)));
  };

  const checkout = async () => {
    if (!cartLines.length) return;
    if (!revolutEnabled) {
      toast.error("Online checkout unavailable", {
        description: "Contact sales@aivoryems.com to purchase add-ons on this environment.",
      });
      return;
    }
    setBusy(true);
    try {
      const addOnIds = cart.flatMap((l) => Array.from({ length: l.qty }, () => l.id));
      const redirectUrl = `${window.location.origin}/admin/billing?payment=success`;
      const res = await fetch(`${API_BASE}/api/admin/billing/checkout`, {
        method: "POST",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ addOnIds, redirectUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Checkout failed");
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      toast.success("Invoice created", { description: "Complete payment from billing history." });
      setCart([]);
      onCheckoutComplete?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline mb-2">Add-ons & services</div>
          <h2 className="font-heading text-2xl tracking-tight">Build your cart</h2>
          <p className="mt-2 text-xs text-[var(--text-tertiary)] max-w-xl leading-relaxed">
            Like domain registrars, pick what you need and check out once. Plan upgrades use Subscribe on the cards
            above; Enterprise is sales-assisted only.
          </p>
        </div>
      </div>

      <div className="mt-8 grid xl:grid-cols-[1fr_340px] gap-8 items-start">
        <div className="space-y-8">
          {groupedCatalog.map(([category, items]) => (
            <section key={category}>
              <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">
                {CATEGORY_LABELS[category] || category}
              </h3>
              <div className="grid sm:grid-cols-2 gap-px bg-[var(--border-default)]">
                {items.map((item) => {
                  const inCart = cart.find((l) => l.id === item.id);
                  return (
                    <div
                      key={item.id}
                      className="bg-[var(--bg-surface)] p-5 flex flex-col min-h-[180px]"
                      data-testid={`addon-card-${item.id}`}
                    >
                      <div className="font-medium text-sm">{item.name}</div>
                      <div className="font-mono text-xl mt-2 text-[var(--primary)]">
                        {item.priceLabel || formatMoneyDisplay(item.priceSgd)}
                        {item.period ? (
                          <span className="text-xs text-[var(--text-tertiary)] font-sans">{item.period}</span>
                        ) : null}
                      </div>
                      <p className="mt-3 text-xs text-[var(--text-secondary)] leading-relaxed flex-1">
                        {item.description || "Adds to your workspace after payment."}
                      </p>
                      <button
                        type="button"
                        className={inCart ? "btn-secondary w-full justify-center text-xs mt-4" : "btn-primary w-full justify-center text-xs mt-4"}
                        onClick={() => addToCart(item.id)}
                      >
                        {inCart ? "Add another" : "Add to cart"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <aside className="surface p-5 xl:sticky xl:top-6 border border-[var(--border-default)]" data-testid="billing-cart">
          <div className="flex items-center gap-2 text-overline mb-4">
            <ShoppingCart size={14} className="text-[var(--primary)]" />
            Your cart
          </div>
          {cartLines.length === 0 ? (
            <p className="text-sm text-[var(--text-tertiary)]">No add-ons yet. Browse the catalog and add what you need.</p>
          ) : (
            <ul className="space-y-3">
              {cartLines.map((line) => (
                <li key={line.id} className="border-b border-[var(--border-default)] pb-3">
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="text-[var(--text-primary)] leading-snug">{line.name}</span>
                    <span className="font-mono shrink-0">{formatMoneyDisplay(line.priceSgd * line.qty)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-1 border border-[var(--border-default)]">
                      <button
                        type="button"
                        className="p-1.5 hover:bg-[var(--bg-elev)]"
                        aria-label="Decrease quantity"
                        onClick={() => setQty(line.id, line.qty - 1)}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-2 text-xs font-mono min-w-[2ch] text-center">{line.qty}</span>
                      <button
                        type="button"
                        className="p-1.5 hover:bg-[var(--bg-elev)]"
                        aria-label="Increase quantity"
                        onClick={() => setQty(line.id, line.qty + 1)}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="text-[var(--text-tertiary)] hover:text-red-400 p-1"
                      aria-label="Remove"
                      onClick={() => setQty(line.id, 0)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 pt-4 border-t border-[var(--border-default)] flex justify-between text-sm">
            <span className="text-[var(--text-secondary)]">Subtotal</span>
            <span className="font-mono font-medium">{formatMoneyDisplay(subtotal)}</span>
          </div>
          <p className="mt-2 text-[10px] text-[var(--text-tertiary)]">GST may apply. Charged in SGD via Revolut.</p>
          <button
            type="button"
            className="btn-primary w-full justify-center mt-4 text-sm"
            disabled={busy || !cartLines.length}
            onClick={checkout}
          >
            {busy ? "Opening checkout…" : "Proceed to checkout"}
          </button>
          <p className="mt-4 text-xs text-[var(--text-tertiary)]">
            Need Enterprise or a custom bundle?{" "}
            <Link to="/contact?intent=sales" className="text-[var(--primary)] hover:underline">
              Contact sales
            </Link>
          </p>
        </aside>
      </div>
    </div>
  );
}
